import type { ChildActivity } from '@/utils/childStats';
import { api } from './api';
import { notificationService } from './notification.service';

export type NotificationPriority = 'low' | 'medium' | 'high' | 'urgent';
export type NotificationType = 'academic' | 'behavioral' | 'attendance' | 'payment' | 'event' | 'system';
export type AttendanceStatus = 'present' | 'absent' | 'late' | 'excused';

export interface Child {
  id: string;
  name: string;
  first_name?: string;
  last_name?: string;
  firstName?: string;
  lastName?: string;
  grade_level?: number;
  gradeLevel?: number;
  school_id?: string;
  student_id?: string;
  profileImage?: string;
  currentStreak?: number;
  recentActivity?: {
    lastActive?: string;
    lessonsCompleted?: number;
    assessmentsTaken?: number;
    averageScore?: number;
  };
  recentAchievements?: Array<{ id: string; name: string; icon?: string }>;
}

export interface ParentNotification {
  id: string;
  parent_id: string;
  child_id?: string;
  child_name?: string;
  type: NotificationType;
  priority: NotificationPriority;
  title: string;
  message: string;
  action_url?: string;
  is_read: boolean;
  requires_response: boolean;
  created_at: string;
  read_at?: string;
  metadata: Record<string, any>;
}

export interface ParentPortalResponse {
  children: any[];
  notifications: ParentNotification[];
  unread_notifications: number;
  attendance_summary: Record<string, any>;
  child_performance: Record<string, any>;
  recent_grades: any[];
  grade_summaries: Record<string, any[]>;
  upcoming_meetings: any[];
  recent_communications: any[];
  upcoming_events: any[];
  registered_events: any[];
  recent_activities?: ChildActivity[];
  portal_settings?: any;
  total_children: number;
  urgent_notifications: number;
  pending_meetings: number;
  upcoming_events_count: number;
}

class ParentService {
  // WebSocket connection
  private websocket: WebSocket | null = null;
  private notificationCallbacks: ((notification: any) => void)[] = [];

  async getParentPortal(): Promise<ParentPortalResponse> {
    const response = await api.get<ParentPortalResponse>('/parent/portal');
    return response.data;
  }

  async getNotifications(params?: {
    skip?: number;
    limit?: number;
    unread_only?: boolean;
    priority?: NotificationPriority;
    type?: NotificationType;
  }): Promise<ParentNotification[]> {
    const response = await api.get<ParentNotification[]>('/parent/notifications', { params });
    return response.data;
  }

  async markNotificationAsRead(notificationId: string): Promise<ParentNotification> {
    const response = await api.patch<ParentNotification>(`/parent/notifications/${notificationId}`, {
      is_read: true,
      read_at: new Date().toISOString()
    });
    return response.data;
  }

  // WebSocket methods for real-time notifications
  connectToNotifications(token: string): void {
    if (this.websocket) {
      this.websocket.close();
    }

    const wsUrl = `${import.meta.env.VITE_WS_URL || 'ws://localhost:8000'}/api/v1/ws/notifications?token=${token}`;
    this.websocket = new WebSocket(wsUrl);

    this.websocket.onopen = () => {
    };

    this.websocket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      this.notificationCallbacks.forEach(callback => callback(data));
    };

    this.websocket.onclose = () => {
      // Attempt to reconnect after 5 seconds
      setTimeout(() => {
        if (this.websocket?.readyState === WebSocket.CLOSED) {
          this.connectToNotifications(token);
        }
      }, 5000);
    };

    this.websocket.onerror = (error) => {
      console.error('[ParentService] WebSocket error:', error);
    };
  }

  disconnectFromNotifications(): void {
    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }
  }

  onNotification(callback: (notification: any) => void): void {
    this.notificationCallbacks.push(callback);
  }

  removeNotificationCallback(callback: (notification: any) => void): void {
    this.notificationCallbacks = this.notificationCallbacks.filter(cb => cb !== callback);
  }

  // Helper methods
  // "il y a 5 h" in the reader's language, like the bell
  formatNotificationTime(timestamp: string): string {
    return notificationService.formatTimeAgo(timestamp);
  }

  getPriorityColor(priority: NotificationPriority): string {
    const colors: Record<NotificationPriority, string> = {
      low: 'text-gray-600',
      medium: 'text-blue-600',
      high: 'text-orange-600',
      urgent: 'text-red-600'
    };
    return colors[priority] || colors.medium;
  }

  getPriorityIcon(priority: NotificationPriority): string {
    const icons: Record<NotificationPriority, string> = {
      low: 'info',
      medium: 'bell',
      high: 'alert-triangle',
      urgent: 'alert-circle'
    };
    return icons[priority] || icons.medium;
  }

  getNotificationTypeIcon(type: NotificationType): string {
    const icons: Record<NotificationType, string> = {
      academic: 'book-open',
      behavioral: 'user-check',
      attendance: 'calendar-check',
      payment: 'credit-card',
      event: 'calendar',
      system: 'settings'
    };
    return icons[type] || icons.system;
  }

  /** Link a child with the child's own login and password (proof of care). */
  async linkChild(login: string, password: string): Promise<{ message: string; child_name: string; child_email: string }> {
    const response = await api.post('/parent/children/link', { login, password });
    return response.data;
  }

  /** Independent parents: create the child's account (login returned once). */
  async addChild(data: { full_name: string; grade_level: string }): Promise<{
    student_id: string; user_id: string; full_name: string; grade_level: string; email: string; temporary_password: string | null
  }> {
    const response = await api.post('/parent/children', data);
    return response.data;
  }

  async unlinkChild(childId: string): Promise<{ message: string }> {
    const response = await api.delete(`/parent/children/${childId}/unlink`);
    return response.data;
  }
}

export const parentService = new ParentService();